
import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Grid,
  Paper,
  Typography,
  Divider,
  CircularProgress,
  Stack,
  Chip,
  LinearProgress,
} from "@mui/material";

import {
  Hotel,
  Business,
  Phone,
  RestaurantMenu,
  FreeBreakfast,
  LunchDining,
  Coffee,
  DinnerDining,
  AccessTime,
  Bed,
  MeetingRoom,
  HotelOutlined,
  EventAvailable,
  NoMeals,
} from "@mui/icons-material";

import HeaderMenu from "../Header/HeaderMenu";
import axiosApi from "../Utils/axiosApi";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const HotelOperatorDashboard = () => {
  const [operatorData, setOperatorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --------------------------------
  // FETCH DASHBOARD DATA
  // --------------------------------

  const fetchDashboardData = async () => {
    try {
      setError(null);

      const today = new Date();

      const currentDate =
        today.getFullYear() +
        "-" +
        String(today.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(today.getDate()).padStart(2, "0");

      const res = await axiosApi.post(
        "/dashBoard/getHotelOperatorSummaryDashboard",
        { currentDate }
      );

      const dto = res.data ?? {};

      const mapped = [
        {
          operatorName:
            dto.operatorCompanyName ?? "Unknown Operator",

          hostels: Array.isArray(dto.hostelDashBoardDTOs)
            ? dto.hostelDashBoardDTOs.map((h) => ({
                id: h.hostelId ?? 0,
                name: h.hostelName ?? "Unnamed Hotel",
                mobile: h.mobile ?? "--",

                hotelClosure: h.hostelClosure === true,

                capacity: Number(h.totalCapacity ?? 0),
                occupied: Number(h.occupied ?? 0),
                availableVacancy: Number(h.availableVacancy ?? 0),

                messCountTotal: h.messCountTotal ?? 0,

                todayMenu: {
                  breakfast: h.todayBreakfastMenu || "N/A",
                  lunch: h.todayLunchMenu || "N/A",
                  snacks: h.todaySnacksMenu || "N/A",
                  dinner: h.todayDinnerMenu || "N/A",
                },

                timings: {
                  breakfast: h.todayBreakfastTime || "--",
                  lunch: h.todayLunchTime || "--",
                  snacks: h.todaySnacksTime || "--",
                  dinner: h.todayDinnerTime || "--",
                },

                todayAddOns: Array.isArray(
                  h.hostelAddOnMenuItemDTOs
                )
                  ? h.hostelAddOnMenuItemDTOs
                  : [],
              }))
            : [],
        },
      ];

      setOperatorData(mapped);
    } catch (e) {
      console.error("Dashboard API Error:", e);
      setError("Unable to load dashboard. Please try again.");
    }
  };

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      await fetchDashboardData();
      setLoading(false);
    };

    loadDashboard();
  }, []);

  // --------------------------------
  // LOADING STATE
  // --------------------------------

  if (loading) {
    return (
      <>
        <HeaderMenu />

        <Box
          minHeight="75vh"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
        >
          <CircularProgress size={45} />

          <Typography color="text.secondary">
            Loading hotel dashboard...
          </Typography>
        </Box>
      </>
    );
  }

  // --------------------------------
  // ERROR STATE
  // --------------------------------

  if (error) {
    return (
      <>
        <HeaderMenu />

        <Box p={4} textAlign="center">
          <Typography color="error" fontWeight={600}>
            {error}
          </Typography>
        </Box>
      </>
    );
  }

  // --------------------------------
  // EMPTY STATE
  // --------------------------------

  const hasHotels = operatorData.some(
    (operator) => operator.hostels.length > 0
  );

  if (!hasHotels) {
    return (
      <>
        <HeaderMenu />

        <Box
          minHeight="65vh"
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          gap={2}
        >
          <HotelOutlined
            sx={{ fontSize: 65, color: "text.disabled" }}
          />

          <Typography variant="h6" fontWeight={700}>
            No Hotel Data Available
          </Typography>

          <Typography color="text.secondary">
            Hotel dashboard information is currently unavailable.
          </Typography>
        </Box>
      </>
    );
  }

  // --------------------------------
  // SUMMARY CARD
  // --------------------------------

  const SummaryCard = ({
    title,
    value,
    subtitle,
    icon,
    background,
    color,
  }) => (
    <Card
      elevation={0}
      sx={{
        p: 2.5,
        height: "100%",
        borderRadius: 3,
        border: "1px solid #e5e7eb",
        backgroundColor: background,
        transition: "0.2s",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 5px 16px rgba(0,0,0,0.07)",
        },
      }}
    >
      <Stack spacing={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            fontSize={13}
            fontWeight={700}
            color="text.secondary"
          >
            {title}
          </Typography>

          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#ffffff",
              color,
            }}
          >
            {icon}
          </Box>
        </Stack>

        <Typography
          variant="h4"
          fontWeight={800}
          color="text.primary"
        >
          {value}
        </Typography>

        {subtitle && (
          <Typography fontSize={12} color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Stack>
    </Card>
  );

  // --------------------------------
  // TODAY MENU & TIMINGS
  // --------------------------------

  const MealCard = ({ meal }) => (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: "100%",
        borderRadius: 2.5,
        borderColor: "#e5e7eb",
        backgroundColor: "#fafbfc",
        transition: "0.2s",

        "&:hover": {
          borderColor: "#90caf9",
          backgroundColor: "#f5f9ff",
        },
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 42,
              height: 42,
              flexShrink: 0,
              borderRadius: 2,
              backgroundColor: "#e8f0fe",
              color: "#2563eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {meal.icon}
          </Box>

          <Typography fontWeight={800} fontSize={15}>
            {meal.title}
          </Typography>
        </Stack>

        <Box>
          <Typography
            fontSize={11}
            color="text.secondary"
            fontWeight={700}
            mb={0.7}
          >
            MENU DETAILS
          </Typography>

          <Typography
            fontSize={14}
            fontWeight={600}
            color="text.primary"
            sx={{
              lineHeight: 1.7,
              overflowWrap: "anywhere",
              minHeight: 44,
            }}
          >
            {meal.menu || "N/A"}
          </Typography>
        </Box>

        <Divider />

        <Box>
          <Stack direction="row" alignItems="center" spacing={0.7}>
            <AccessTime
              sx={{ fontSize: 17, color: "text.secondary" }}
            />

            <Typography
              fontSize={11}
              fontWeight={700}
              color="text.secondary"
            >
              SERVING TIME
            </Typography>
          </Stack>

          <Typography
            fontSize={14}
            fontWeight={700}
            color="primary.main"
            mt={0.7}
          >
            {meal.time || "--"}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  const TodayMenuSection = ({ hostel }) => {
    const meals = [
      {
        title: "Breakfast",
        menu: hostel.todayMenu.breakfast,
        time: hostel.timings.breakfast,
        icon: <FreeBreakfast />,
      },
      {
        title: "Lunch",
        menu: hostel.todayMenu.lunch,
        time: hostel.timings.lunch,
        icon: <LunchDining />,
      },
      {
        title: "Snacks",
        menu: hostel.todayMenu.snacks,
        time: hostel.timings.snacks,
        icon: <Coffee />,
      },
      {
        title: "Dinner",
        menu: hostel.todayMenu.dinner,
        time: hostel.timings.dinner,
        icon: <DinnerDining />,
      },
    ];

    return (
      <Card
        elevation={0}
        sx={{
          mt: 3,
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: "1px solid #e5e7eb",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1}
          mb={2}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <RestaurantMenu color="primary" />

            <Typography fontWeight={800} fontSize={18}>
              Today's Food Menu
            </Typography>
          </Stack>

          <Chip
            size="small"
            label="Daily Schedule"
            variant="outlined"
            color="primary"
          />
        </Stack>

        <Divider sx={{ mb: 2.5 }} />

        <Grid container spacing={2}>
          {meals.map((meal) => (
            <Grid item xs={12} sm={6} lg={3} key={meal.title}>
              <MealCard meal={meal} />
            </Grid>
          ))}
        </Grid>
      </Card>
    );
  };

  // --------------------------------
  // MAIN DASHBOARD
  // --------------------------------

  return (
    <>
      <HeaderMenu />

      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#f5f7fb",
          p: { xs: 1.5, sm: 2.5, md: 3 },
        }}
      >
        {/* PAGE HEADER */}

        <Box mb={3}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={1}
          >
            <Box>
              <Typography
                variant="h4"
                fontWeight={800}
                sx={{ fontSize: { xs: 23, md: 30 } }}
              >
                Hotel Dashboard
              </Typography>

              <Typography
                color="text.secondary"
                fontSize={13}
                mt={0.5}
              >
                Monitor hotel occupancy, food menu and daily operations.
              </Typography>
            </Box>

            <Chip
              icon={<EventAvailable />}
              label="Today's Overview"
              color="primary"
              variant="outlined"
            />
          </Stack>
        </Box>

        {/* OPERATOR INFORMATION */}

        {operatorData.map((op, operatorIndex) => (
          <Box key={`${op.operatorName}-${operatorIndex}`} mb={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2.5,
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    width: 45,
                    height: 45,
                    borderRadius: 2,
                    backgroundColor: "#e8f0fe",
                    color: "#2563eb",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Business />
                </Box>

                <Box>
                  <Typography
                    fontSize={11}
                    fontWeight={700}
                    color="text.secondary"
                  >
                    HOTEL OPERATOR
                  </Typography>

                  <Typography variant="h6" fontWeight={800}>
                    {op.operatorName}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* HOTEL CARDS */}

            {op.hostels.map((h) => {
              const occupancyPercent = h.capacity
                ? Math.min(
                    (h.occupied / h.capacity) * 100,
                    100
                  )
                : 0;

              const chartData = [
                {
                  category: "Rooms",
                  Capacity: h.capacity,
                  Occupied: h.occupied,
                  Vacancy: h.availableVacancy,
                },
              ];

              return (
                <Card
                  key={h.id}
                  elevation={0}
                  sx={{
                    p: { xs: 2, md: 3 },
                    mb: 3,
                    borderRadius: 3,
                    border: "1px solid #e5e7eb",
                    backgroundColor: "#ffffff",
                  }}
                >
                  {/* HOTEL HEADER */}

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1.5}
                    mb={2.5}
                  >
                    <Box>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                      >
                        <Hotel color="primary" />

                        <Typography
                          variant="h6"
                          fontWeight={800}
                        >
                          {h.name}
                        </Typography>
                      </Stack>

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.7}
                        mt={0.8}
                      >
                        <Phone
                          sx={{
                            fontSize: 16,
                            color: "text.secondary",
                          }}
                        />

                        <Typography
                          fontSize={13}
                          color="text.secondary"
                        >
                          {h.mobile}
                        </Typography>
                      </Stack>
                    </Box>

                    <Chip
                      label={
                        h.hotelClosure
                          ? "Closed Today"
                          : "Operational"
                      }
                      color={h.hotelClosure ? "warning" : "success"}
                      variant="outlined"
                    />
                  </Stack>

                  {/* HOTEL CLOSED */}

                  {h.hotelClosure && (
                    <Paper
                      sx={{
                        p: 1.5,
                        mb: 2.5,
                        backgroundColor: "#fff7ed",
                        border: "1px solid #fed7aa",
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        fontWeight={700}
                        color="#c2410c"
                        fontSize={14}
                      >
                        Hotel Closed Today
                      </Typography>
                    </Paper>
                  )}

                  {/* OCCUPANCY SUMMARY */}

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <SummaryCard
                        title="Occupied"
                        value={h.occupied}
                        subtitle={`${occupancyPercent.toFixed(1)}% occupancy`}
                        icon={<Hotel />}
                        background="#eff6ff"
                        color="#2563eb"
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <SummaryCard
                        title="Available Vacancy"
                        value={h.availableVacancy}
                        subtitle="Available capacity"
                        icon={<Bed />}
                        background="#f0fdf4"
                        color="#16a34a"
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <SummaryCard
                        title="Total Capacity"
                        value={h.capacity}
                        subtitle="Total available capacity"
                        icon={<MeetingRoom />}
                        background="#fff7ed"
                        color="#ea580c"
                      />
                    </Grid>
                  </Grid>

                  {/* OCCUPANCY PROGRESS */}

                  <Paper
                    elevation={0}
                    sx={{
                      mt: 2.5,
                      p: 2,
                      border: "1px solid #e5e7eb",
                      borderRadius: 2.5,
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <Typography fontWeight={700} fontSize={13}>
                        Occupancy Rate
                      </Typography>

                      <Typography
                        fontWeight={800}
                        fontSize={13}
                        color="primary.main"
                      >
                        {occupancyPercent.toFixed(1)}%
                      </Typography>
                    </Stack>

                    <LinearProgress
                      variant="determinate"
                      value={occupancyPercent}
                      sx={{
                        height: 9,
                        borderRadius: 5,
                        backgroundColor: "#e5e7eb",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 5,
                          backgroundColor: "#2563eb",
                        },
                      }}
                    />
                  </Paper>

                  {/* TODAY MENU */}

                  <TodayMenuSection hostel={h} />

                  {/* ADD ON ITEMS */}

                  <Card
                    elevation={0}
                    sx={{
                      mt: 3,
                      p: 2.5,
                      borderRadius: 3,
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={2}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                      >
                        <NoMeals color="primary" />

                        <Typography fontWeight={800} fontSize={17}>
                          Add-On Items
                        </Typography>
                      </Stack>

                      <Chip
                        size="small"
                        label={`${h.todayAddOns.length} Items`}
                        variant="outlined"
                      />
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    {h.todayAddOns.length > 0 ? (
                      <Grid container spacing={1.5}>
                        {h.todayAddOns.map((item, index) => (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            key={item.id ?? index}
                          >
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: "#fafbfc",
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={1}
                              >
                                <Typography
                                  fontWeight={600}
                                  fontSize={13}
                                >
                                  {item.itemName || "Unnamed Item"}
                                </Typography>

                                <Typography
                                  fontWeight={800}
                                  color="success.main"
                                  fontSize={14}
                                >
                                  ₹{item.itemPrice ?? 0}
                                </Typography>
                              </Stack>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography
                        fontSize={13}
                        color="text.secondary"
                        textAlign="center"
                        py={2}
                      >
                        No add-on items available today.
                      </Typography>
                    )}
                  </Card>

                  {/* ROOM CHART */}

                  <Card
                    elevation={0}
                    sx={{
                      mt: 3,
                      p: { xs: 2, md: 2.5 },
                      borderRadius: 3,
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      mb={2}
                    >
                      <HotelOutlined color="primary" />

                      <Typography fontWeight={800} fontSize={18}>
                        Room Overview
                      </Typography>
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    <Box width="100%" height={300}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={chartData}
                          margin={{
                            top: 10,
                            right: 15,
                            left: 0,
                            bottom: 5,
                          }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                          />

                          <XAxis
                            dataKey="category"
                            tick={{ fontSize: 12 }}
                          />

                          <YAxis
                            allowDecimals={false}
                            tick={{ fontSize: 12 }}
                          />

                          <Tooltip
                            contentStyle={{
                              borderRadius: 10,
                              border: "1px solid #e5e7eb",
                              fontSize: 13,
                            }}
                          />

                          <Legend
                            wrapperStyle={{
                              fontSize: 12,
                            }}
                          />

                          <Bar
                            dataKey="Capacity"
                            fill="#90caf9"
                            radius={[5, 5, 0, 0]}
                          />

                          <Bar
                            dataKey="Occupied"
                            fill="#66bb6a"
                            radius={[5, 5, 0, 0]}
                          />

                          <Bar
                            dataKey="Vacancy"
                            fill="#ef5350"
                            radius={[5, 5, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Card>
                </Card>
              );
            })}
          </Box>
        ))}
      </Box>
    </>
  );
};

export default HotelOperatorDashboard;
